import { ArrowRight, Check, Code2, FileText, MousePointer2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../button';
import { Input } from '../../input';

import { Lab, Reset, Result } from './lab-parts';
import { ModelSelect as SelectField } from './learning-model-surface';
import { normalizeTitle, validTitle } from './title-model';
import {
  acceptanceFrame,
  acceptanceSteps,
  actionFrame,
  apiValidationFrame,
  awaitFrame,
  awaitSteps,
  creationFrame,
  inputFrame,
  layoutFrame,
  lockedSteps,
  lockingFrame,
  orderingFrame,
  originFrame,
  readFrame,
  registrationFrame,
  registrationSteps,
  resourceFrame,
  responsibilityFrame,
  responsibilitySteps,
  unlockedSteps,
  valueChange,
  type AcceptanceOutcome,
  type AwaitOutcome,
  type CreationOutcome,
  type InputExample,
  type LockScope,
  type ModelFrame,
  type ReadOutcome,
  type ResourceOperation,
  type TaskView,
  type ValidationBody,
} from './web-discovery-models';

import './web-discovery-labs.css';

import type { ReactNode } from 'react';

interface Choice {
  value: string;
  label: string;
}
const choice = (value: string, label: string): Choice => ({ value, label });
function Select({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  options: Choice[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <label>
      {label}
      <SelectField
        aria-label={label}
        value={value}
        options={options}
        onValueChange={onChange}
        disabled={disabled}
      />
    </label>
  );
}
function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="wd-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Output({ label, value }: { label: string; value: string }) {
  return (
    <div className="wd-output" role="group" aria-label={label}>
      <small>{label}</small>
      <pre>{value}</pre>
    </div>
  );
}
function Frame({ frame }: { frame: ModelFrame }) {
  return (
    <>
      <div className="wd-outputs">
        {frame.outputs.map((item) => (
          <Output key={item.label} {...item} />
        ))}
      </div>
      <Result tone={frame.warning ? 'warning' : 'neutral'}>{frame.explanation}</Result>
    </>
  );
}
function Steps({ labels, step }: { labels: readonly string[]; step: number }) {
  return (
    <ol className="wd-steps" aria-label="Mechanism steps">
      {labels.map((label, index) => (
        <li
          key={label}
          data-done={index < step}
          aria-current={index === step - 1 ? 'step' : undefined}
        >
          <span>{index < step ? <Check size={12} aria-hidden="true" /> : index + 1}</span>
          <div>
            <small>{index < step ? 'Done' : 'Next'}</small>
            {label}
          </div>
        </li>
      ))}
    </ol>
  );
}
function Next({
  onClick,
  disabled,
  children = 'Advance one step',
}: {
  onClick: () => void;
  disabled: boolean;
  children?: ReactNode;
}) {
  return (
    <Button type="button" variant="secondary" size="sm" onClick={onClick} disabled={disabled}>
      {children}
      <ArrowRight size={13} aria-hidden="true" />
    </Button>
  );
}
function TraceLab({
  title,
  predict,
  label,
  options,
  steps,
  frame,
  note,
}: {
  title: string;
  predict: string;
  label: string;
  options: Choice[];
  steps: readonly string[] | ((value: string) => readonly string[]);
  frame: (value: string, step: number) => ModelFrame;
  note?: string;
}) {
  const first = options[0]?.value ?? '';
  const [value, setValue] = useState(first);
  const [step, setStep] = useState(0);
  const labels = typeof steps === 'function' ? steps(value) : steps;
  return (
    <Lab
      title={title}
      eyebrow="Experiment · follow the mechanism"
      className="wd-lab"
      {...(note ? { note } : {})}
    >
      <Predict>{predict}</Predict>
      <div className="cb-controls">
        <Select
          label={label}
          value={value}
          options={options}
          onChange={(next) => {
            setValue(next);
            setStep(0);
          }}
        />
      </div>
      <Steps labels={labels} step={step} />
      <Frame frame={frame(value, step)} />
      <div className="cb-controls">
        <Next disabled={step >= labels.length} onClick={() => setStep(step + 1)}>
          {labels[step] ?? 'Trace complete'}
        </Next>
        <Reset
          onClick={() => {
            setValue(first);
            setStep(0);
          }}
        />
      </div>
    </Lab>
  );
}

export function ApiResponsibilitiesLab() {
  return (
    <TraceLab
      title="The work remains when the tool changes"
      predict="Which work belongs to the framework, and which promise still belongs to your application?"
      label="HTTP implementation"
      options={[choice('manual', 'Our socket server'), choice('framework', 'Uvicorn + FastAPI')]}
      steps={responsibilitySteps}
      frame={(value, step) => responsibilityFrame(value === 'framework', step)}
    />
  );
}
export function ApiRegistrationLab() {
  return (
    <TraceLab
      title="Register now, call on a request"
      predict="After startup, how many times has the handler run? Will a direct function call create a route?"
      label="Startup instruction"
      options={[
        choice('register', 'Register with a decorator'),
        choice('call', 'Call health() directly'),
      ]}
      steps={(value) =>
        value === 'call'
          ? registrationSteps.map((label, index) =>
              index === 2 ? 'Call health() during setup' : label,
            )
          : registrationSteps
      }
      frame={(value, step) => registrationFrame(value === 'call', step)}
    />
  );
}
export function ApiInputsLab() {
  return (
    <TraceLab
      title="Where does this argument come from?"
      predict="Does the word false in a URL reach the handler as text, a boolean, or an absent value?"
      label="Request example"
      options={[
        choice('path', 'Path: task 7'),
        choice('query', 'Query: completed=false'),
        choice('omitted', 'Query filter omitted'),
        choice('body', 'Body: create a title'),
      ]}
      steps={['Locate the input', 'Bind the declared argument']}
      frame={(value, step) => inputFrame(value as InputExample, step)}
    />
  );
}
export function ApiValidationLab() {
  return (
    <TraceLab
      title="Valid JSON is only the first boundary"
      predict="Which bodies may reach the handler? Compare false with the string “false”."
      label="Body to inspect"
      options={[
        choice('valid', 'Title with surrounding spaces'),
        choice('blank', 'Whitespace-only title'),
        choice('number', 'Title is a number'),
        choice('missing', 'Missing title'),
        choice('extra', 'Client supplies an id'),
        choice('string', 'Completion is a string'),
        choice('boolean', 'Completion is a boolean'),
        choice('array', 'Body is an array'),
      ]}
      steps={['Apply request-model rules', 'Inspect the handler boundary']}
      frame={(value, step) => apiValidationFrame(value as ValidationBody, step)}
    />
  );
}
export function ApiResourcesLab() {
  return (
    <TraceLab
      title="Repeat the same request"
      predict="Does repeating set completed=true have the same effect as repeating toggle?"
      label="Resource operation"
      options={[
        choice('set', 'PATCH: set completed to true'),
        choice('toggle', 'Compare: imaginary toggle command'),
        choice('get', 'GET: existing task 7'),
        choice('missing', 'GET: absent task 99'),
        choice('delete', 'DELETE: task 7'),
        choice('filter', 'GET: completed view is empty'),
        choice('invalid-id', 'GET: invalid path ID'),
        choice('unsupported', 'PUT: unsupported method'),
      ]}
      steps={['Apply once', 'Repeat the same operation']}
      frame={(value, step) => resourceFrame(value as ResourceOperation, step)}
      note="Repeat an action and compare its effect on state. DELETE returns 404 once the record is absent."
    />
  );
}
export function ApiAcceptanceLab() {
  return (
    <TraceLab
      title="Saving, accepting and confirming are different events"
      predict="If the reply disappears after saving, does the task disappear too?"
      label="Create outcome"
      options={[
        choice('saved', 'Save and reply succeed'),
        choice('failed', 'Save fails before replacement'),
        choice('lost', 'Save succeeds; response is lost'),
      ]}
      steps={(value) =>
        value === 'failed'
          ? acceptanceSteps.map((label, index) =>
              index === 2 ? 'Keep current data after failed save' : label,
            )
          : acceptanceSteps
      }
      frame={(value, step) => acceptanceFrame(value as AcceptanceOutcome, step)}
    />
  );
}
export function ApiLockingLab() {
  return (
    <TraceLab
      title="A safe file write can still lose a task"
      predict="Both requests copy next_id=4. Can locking only their file writes repair those stale copies?"
      label="Lock scope"
      options={[
        choice('none', 'No shared lock'),
        choice('write', 'Lock only the file write'),
        choice('whole', 'Lock the whole change'),
      ]}
      steps={(value) => (value === 'whole' ? lockedSteps : unlockedSteps)}
      frame={(value, step) => lockingFrame(value as LockScope, step)}
      note="Step through these overlapping requests to see one overwrite the other. A shared lock makes requests take turns within one server process."
    />
  );
}

export function WebLayersLab() {
  const [html, setHtml] = useState(true);
  const [css, setCss] = useState(false);
  const [js, setJs] = useState(false);
  const [count, setCount] = useState(0);
  const [tried, setTried] = useState(false);
  return (
    <Lab
      title="Three layers, three different jobs"
      eyebrow="Experiment · document, presentation, behavior"
      className="wd-lab"
    >
      <Predict>If you remove styling after adding a row, should the row stop existing?</Predict>
      <div className="wd-toggles">
        {[
          { name: 'HTML', enabled: html, toggle: () => setHtml(!html) },
          { name: 'CSS', enabled: css, toggle: () => setCss(!css) },
          { name: 'JavaScript', enabled: js, toggle: () => setJs(!js) },
        ].map((layer) => (
          <Button
            key={layer.name}
            type="button"
            size="sm"
            variant="secondary"
            aria-pressed={layer.enabled}
            onClick={layer.toggle}
          >
            {layer.name}: {layer.enabled ? 'on' : 'off'}
          </Button>
        ))}
      </div>
      <div className="wd-document" data-styled={css} role="group" aria-label="Document preview">
        {html ? (
          <>
            <h4>CollabBoard</h4>
            <p>Task title: Read notes</p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setTried(true);
                if (js) setCount(count + 1);
              }}
            >
              Add sample row
            </Button>
            {Array.from({ length: count }, (_, index) => (
              <p key={index}>#{index + 1} Read notes</p>
            ))}
          </>
        ) : (
          <p>No task document is present. Styling and behavior alone do not provide this form.</p>
        )}
      </div>
      <Output label="Document rows retained in this model" value={String(count)} />
      <Result>
        {!html
          ? 'The model hides the document while preserving its sample data. A real page reload would run its scripts again.'
          : tried && !js
            ? 'The button exists, but no JavaScript handler adds a row. CSS can change appearance, not implement this task action.'
            : count
              ? 'JavaScript added rows to the document. Turning CSS off changes presentation; the rows still exist.'
              : 'Enable behavior, then use Add sample row. These are local document rows, not saved API tasks.'}
      </Result>
      <Reset
        onClick={() => {
          setHtml(true);
          setCss(false);
          setJs(false);
          setCount(0);
          setTried(false);
        }}
      />
    </Lab>
  );
}

const structureParts = [
  {
    id: 'form',
    label: 'form',
    code: '<form id="create-task">…</form>',
    meaning:
      'Groups the title and submit button into one submission action. Enter can submit the form.',
  },
  {
    id: 'label',
    label: 'label → input',
    code: '<label for="title">Task title</label>\n<input id="title" name="title">',
    meaning:
      'The matching for/id connects the visible label to this control. A placeholder is not an equivalent persistent label.',
  },
  {
    id: 'button',
    label: 'button',
    code: '<button type="submit">Add task</button>',
    meaning: 'A native submit button participates in the form and keyboard interaction.',
  },
  {
    id: 'status',
    label: 'status',
    code: '<p role="status">Saving…</p>',
    meaning:
      'The status area communicates a change without moving focus. It does not save anything by itself.',
  },
  {
    id: 'list',
    label: 'ul → li',
    code: '<ul id="tasks">\n  <li>Read notes</li>\n</ul>',
    meaning:
      'The list groups task rows; each li is one item. Nested structure communicates the relationship independently of visual spacing.',
  },
] as const;
export function WebStructureLab() {
  const [part, setPart] = useState(0);
  const [semantic, setSemantic] = useState(true);
  const [pressed, setPressed] = useState(false);
  const entry = structureParts[part] ?? structureParts[0];
  return (
    <Lab
      title="A page is a tree with meaning"
      eyebrow="Experiment · HTML semantics"
      className="wd-lab"
    >
      <Predict>
        Two controls can look alike. Will Enter in the title field work the same way?
      </Predict>
      <div className="wd-tree" aria-label="Document tree">
        <strong>main</strong>
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={part === 0}
            onClick={() => setPart(0)}
          >
            form
          </Button>
          <div>
            {structureParts.slice(1, 3).map((item, index) => (
              <Button
                key={item.id}
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={part === index + 1}
                onClick={() => setPart(index + 1)}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={part === 3}
          onClick={() => setPart(3)}
        >
          status
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={part === 4}
          onClick={() => setPart(4)}
        >
          ul → li
        </Button>
      </div>
      <Output label="Selected HTML" value={entry.code} />
      <p>{entry.meaning}</p>
      <div className="cb-controls">
        <Select
          label="Control structure"
          value={semantic ? 'form' : 'shapes'}
          options={[
            choice('form', 'Labelled form + submit button'),
            choice('shapes', 'Styled shapes without a form'),
          ]}
          onChange={(value) => {
            setSemantic(value === 'form');
            setPressed(false);
          }}
        />
        <Next disabled={false} onClick={() => setPressed(true)}>
          Model Enter in the title field
        </Next>
        <Reset
          onClick={() => {
            setPart(0);
            setSemantic(true);
            setPressed(false);
          }}
        />
      </div>
      <Result>
        {!pressed
          ? 'Select a node to inspect its job, then compare the keyboard behavior.'
          : semantic
            ? 'A submit event is produced for the form. JavaScript can listen for it and prevent the default navigation.'
            : 'These shapes do not define a form submission. A click handler alone does not provide a form’s keyboard behavior or field labels.'}
      </Result>
    </Lab>
  );
}

export function WebLayoutLab() {
  const [width, setWidth] = useState('360');
  const [padding, setPadding] = useState('12');
  const [wrap, setWrap] = useState('wrap');
  const [title, setTitle] = useState('short');
  const frame = layoutFrame(Number(width), Number(padding), wrap === 'wrap');
  return (
    <Lab
      title="Where did the available width go?"
      eyebrow="Experiment · boxes and wrapping"
      className="wd-lab"
    >
      <Predict>
        Make the container narrow, then increase its padding. Can the controls still fit on one
        line?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Container width"
          value={width}
          onChange={setWidth}
          options={[choice('360', '360 px'), choice('280', '280 px'), choice('220', '220 px')]}
        />
        <Select
          label="Padding on each side"
          value={padding}
          onChange={setPadding}
          options={[choice('12', '12 px'), choice('24', '24 px'), choice('36', '36 px')]}
        />
        <Select
          label="Flex wrapping"
          value={wrap}
          onChange={setWrap}
          options={[choice('wrap', 'Allow wrapping'), choice('nowrap', 'Keep one row')]}
        />
        <Select
          label="Task text"
          value={title}
          options={[choice('short', 'Short title'), choice('long', 'Long title')]}
          onChange={setTitle}
        />
      </div>
      <div className="wd-layout-stage">
        <div
          className="wd-layout-box"
          style={{
            width: Number(width),
            padding: Number(padding),
            flexWrap: wrap === 'wrap' ? 'wrap' : 'nowrap',
          }}
          role="group"
          aria-label="Layout preview"
        >
          <span
            className="wd-layout-input"
            style={{ width: wrap === 'wrap' ? Math.min(180, frame.available) : 180 }}
          >
            Task title
          </span>
          <span className="wd-layout-button">Add</span>
        </div>
        <div
          className="wd-layout-title"
          style={{ width: Number(width), padding: Number(padding) }}
          aria-label="Task text preview"
        >
          {title === 'short'
            ? 'Read notes'
            : 'Read the HTTP notes and explain why a response can arrive after the page changes.'}
        </div>
      </div>
      <Output
        label="Width budget in this model"
        value={`${width} − 2 × ${padding} = ${frame.available} px inside\nThe two controls and their gap need ${frame.required} px.`}
      />
      <Result tone={frame.overflow ? 'warning' : 'neutral'}>{frame.explanation}</Result>
      <Reset
        onClick={() => {
          setWidth('360');
          setPadding('12');
          setWrap('wrap');
          setTitle('short');
        }}
      />
    </Lab>
  );
}

export function WebValuesLab() {
  const [binding, setBinding] = useState('const');
  const [operation, setOperation] = useState('mutate');
  const [executed, setExecuted] = useState(false);
  const result = valueChange(
    binding === 'const' ? 'const' : 'let',
    operation === 'mutate' ? 'mutate' : 'replace',
  );
  return (
    <Lab
      title="A binding and its object are different things"
      eyebrow="Experiment · JavaScript values"
      className="wd-lab"
    >
      <Predict>Does const mean that no property of this object can change?</Predict>
      <div className="cb-controls">
        <Select
          label="Binding declaration"
          value={binding}
          options={[choice('const', 'const task'), choice('let', 'let task')]}
          onChange={(value) => {
            setBinding(value);
            setExecuted(false);
          }}
        />
        <Select
          label="Next instruction"
          value={operation}
          options={[
            choice('mutate', 'Change task.title'),
            choice('replace', 'Replace the task binding'),
          ]}
          onChange={(value) => {
            setOperation(value);
            setExecuted(false);
          }}
        />
      </div>
      <Output
        label="JavaScript instructions"
        value={`${binding} task = { title: "Read notes" };\n${result.statement};`}
      />
      <div className="wd-binding">
        <Code2 size={18} aria-hidden="true" />
        <strong>task</strong>
        <ArrowRight size={18} aria-hidden="true" />
        <Output
          label="Object reached by task"
          value={JSON.stringify(executed ? result.value : { title: 'Read notes' })}
        />
      </div>
      <div className="cb-controls">
        <Next disabled={executed} onClick={() => setExecuted(true)}>
          Execute next instruction
        </Next>
        <Reset
          onClick={() => {
            setBinding('const');
            setOperation('mutate');
            setExecuted(false);
          }}
        />
      </div>
      <Result tone={executed && !result.allowed ? 'warning' : 'neutral'}>
        {executed
          ? result.allowed
            ? result.explanation
            : `TypeError. ${result.explanation}`
          : 'Predict the object value before running the second instruction.'}
      </Result>
    </Lab>
  );
}

export function WebRenderingLab() {
  const [draft, setDraft] = useState('Read notes');
  const [record, setRecord] = useState('Read notes');
  const [shown, setShown] = useState('Read notes');
  return (
    <Lab
      title="Changing data does not redraw the document"
      eyebrow="Experiment · explicit DOM rendering"
      className="wd-lab"
    >
      <Predict>
        Change the JavaScript record. Does the visible row change before renderTasks runs?
      </Predict>
      <div className="cb-controls">
        <label>
          New title
          <Input
            aria-label="New title"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setDraft('<b>Read HTTP</b>')}
        >
          Try an HTML-like title
        </Button>
      </div>
      <div className="wd-outputs">
        <Output label="JavaScript record" value={JSON.stringify({ id: 7, title: record })} />
        <div className="wd-output" role="group" aria-label="DOM row">
          <small>DOM row</small>
          <div className="wd-rendered-row">
            <FileText size={15} aria-hidden="true" />
            <span>{shown}</span>
          </div>
        </div>
      </div>
      <Output label="Renderer’s key instruction" value="row.textContent = task.title;" />
      <div className="cb-controls">
        <Next disabled={false} onClick={() => setRecord(draft)}>
          Change the record
        </Next>
        <Next disabled={false} onClick={() => setShown(record)}>
          Run renderTasks
        </Next>
        <Reset
          onClick={() => {
            setDraft('Read notes');
            setRecord('Read notes');
            setShown('Read notes');
          }}
        />
      </div>
      <Result>
        {record !== shown
          ? 'The record changed, but the old DOM row remains. Explicit rendering is the missing step.'
          : shown.includes('<')
            ? 'The title appears literally. textContent keeps data separate from HTML instructions.'
            : 'The DOM currently displays this record. Editing the draft field alone changes neither the record nor the rendered row.'}
      </Result>
    </Lab>
  );
}

export function WebEventsLab() {
  const [prevent, setPrevent] = useState('prevent');
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('Read notes');
  const submittedTitle = normalizeTitle(title);
  const steps = ['Person submits the form', 'Run the event listener', 'Finish the event'] as const;
  return (
    <Lab
      title="A submit event has a default action"
      eyebrow="Experiment · form events"
      className="wd-lab"
    >
      <Predict>
        If JavaScript handles submission but never prevents the default, what else will the browser
        try to do?
      </Predict>
      <div className="cb-controls">
        <label>
          Form title
          <Input
            aria-label="Form title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setStep(0);
            }}
          />
        </label>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            setTitle('   ');
            setStep(0);
          }}
        >
          Use a whitespace title
        </Button>
        <Select
          label="Listener behavior"
          value={prevent}
          options={[
            choice('prevent', 'Call event.preventDefault()'),
            choice('default', 'Leave the default action enabled'),
          ]}
          onChange={(value) => {
            setPrevent(value);
            setStep(0);
          }}
        />
      </div>
      <Steps labels={steps} step={step} />
      <div className="wd-event-flow">
        <MousePointer2 size={20} aria-hidden="true" />
        <span>Submit</span>
        <ArrowRight size={16} aria-hidden="true" />
        <span data-active={step >= 2}>Registered listener</span>
        <ArrowRight size={16} aria-hidden="true" />
        <span data-active={step >= 3}>
          {step >= 3
            ? prevent === 'prevent'
              ? 'Stay on this document'
              : 'Default navigation'
            : 'Default action decision'}
        </span>
      </div>
      <Output
        label="Listener code"
        value={`function normalizeTitle(value) {\n  return value.replace(/^[ \\t\\r\\n]+|[ \\t\\r\\n]+$/g, '');\n}\n\nform.addEventListener('submit', (event) => {\n  ${prevent === 'prevent' ? 'event.preventDefault();' : '// default is not prevented'}\n  const submittedTitle = normalizeTitle(title.value);\n  if (!submittedTitle) {\n    status.textContent = 'Enter a title.';\n    return;\n  }\n  status.textContent = 'Ready: ' + submittedTitle;\n});`}
      />
      <div className="wd-outputs">
        <Output
          label="Value read by the listener"
          value={step >= 2 ? JSON.stringify(submittedTitle) : '(listener has not run)'}
        />
        <Output
          label="Local feedback"
          value={
            step >= 2
              ? submittedTitle
                ? `Ready: ${submittedTitle} (not sent to a server)`
                : 'Enter a title.'
              : '(no feedback yet)'
          }
        />
      </div>
      <div className="cb-controls">
        <Next disabled={step >= 3} onClick={() => setStep(step + 1)}>
          {steps[step] ?? 'Event complete'}
        </Next>
        <Reset
          onClick={() => {
            setPrevent('prevent');
            setStep(0);
            setTitle('Read notes');
          }}
        />
      </div>
      <Result tone={step === 3 && prevent === 'default' ? 'warning' : 'neutral'}>
        {step < 3
          ? 'Registering a listener supplies a function for a later event. Clicking or pressing Enter in the form can produce that submit event.'
          : prevent === 'prevent'
            ? 'The script takes responsibility for the submission workflow. Preventing navigation does not itself save a task.'
            : 'The browser can navigate away using its normal form behavior. Merely adding a JavaScript listener does not cancel that default.'}
      </Result>
    </Lab>
  );
}

export function WebOriginLab() {
  return (
    <TraceLab
      title="Whose localhost is it?"
      predict="When the page runs in your browser, does 127.0.0.1 identify the learner workspace?"
      label="Page and request setup"
      options={[
        choice('relative', 'Open app tab + relative /tasks'),
        choice('loopback', 'Open app tab + hard-coded loopback'),
        choice('file', 'Open the HTML file directly'),
      ]}
      steps={['Resolve the request destination']}
      frame={(value, step) =>
        step
          ? originFrame(value as 'relative' | 'loopback' | 'file')
          : {
              outputs: [{ label: 'Destination', value: '(choose a setup, then resolve it)' }],
              explanation:
                'An origin is the combination of scheme, host and port. A root-relative path uses the page’s origin.',
            }
      }
      note="Compare how relative and absolute paths choose a destination from the example page address."
    />
  );
}

export function WebAwaitLab() {
  const [step, setStep] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [outcome, setOutcome] = useState('success');
  const steps =
    outcome === 'network-error'
      ? ['Call fetch', 'Connection fails before a response']
      : outcome === 'http-error'
        ? ['Call fetch', 'Receive response headers', 'Check response.ok']
        : awaitSteps;
  return (
    <Lab
      title="Waiting does not freeze the page"
      eyebrow="Experiment · promises and await"
      className="wd-lab"
    >
      <Predict>
        Can another event handler run while the request function waits for its response?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Fetch outcome"
          value={outcome}
          options={[
            choice('success', 'Successful JSON response'),
            choice('http-error', 'HTTP rejection · 422'),
            choice('network-error', 'Connection failure'),
          ]}
          onChange={(value) => {
            setOutcome(value);
            setStep(0);
            setClicks(0);
          }}
        />
      </div>
      <Steps labels={steps} step={step} />
      <Frame frame={awaitFrame(step, clicks, outcome as AwaitOutcome)} />
      <div className="cb-controls">
        <Next disabled={step >= steps.length} onClick={() => setStep(step + 1)}>
          {steps[step] ?? 'Request finished'}
        </Next>
        <Button type="button" variant="secondary" size="sm" onClick={() => setClicks(clicks + 1)}>
          Run another event handler
        </Button>
        <Reset
          onClick={() => {
            setStep(0);
            setClicks(0);
            setOutcome('success');
          }}
        />
      </div>
    </Lab>
  );
}

export function WebReadStatesLab() {
  const [outcome, setOutcome] = useState('data');
  const [previous, setPrevious] = useState('none');
  const [step, setStep] = useState(0);
  return (
    <Lab
      title="No rows can mean several different things"
      eyebrow="Experiment · loading, empty and failed"
      className="wd-lab"
    >
      <Predict>When a read fails, have we learned that there are no tasks?</Predict>
      <div className="cb-controls">
        <Select
          label="Read outcome"
          value={outcome}
          options={[
            choice('data', '200 with task records'),
            choice('empty', '200 with an empty array'),
            choice('failed', 'Request fails'),
            choice('unreadable', 'Response contains unreadable JSON'),
          ]}
          onChange={(value) => {
            setOutcome(value);
            setStep(0);
          }}
        />
        <Select
          label="Previous successful view"
          value={previous}
          options={[
            choice('none', 'No previous rows'),
            choice('present', 'Previously loaded task'),
          ]}
          onChange={(value) => {
            setPrevious(value);
            setStep(0);
          }}
        />
      </div>
      <Steps labels={['Start the read', 'Receive its result']} step={step} />
      <Frame frame={readFrame(outcome as ReadOutcome, previous === 'present', step)} />
      <div className="cb-controls">
        <Next disabled={step >= 2} onClick={() => setStep(step + 1)}>
          {step ? 'Receive read result' : 'Start reading tasks'}
        </Next>
        <Reset
          onClick={() => {
            setOutcome('data');
            setPrevious('none');
            setStep(0);
          }}
        />
      </div>
    </Lab>
  );
}

export function WebCreationLab() {
  const [outcome, setOutcome] = useState('saved');
  const [title, setTitle] = useState('Read notes');
  const [step, setStep] = useState(0);
  const invalid = !validTitle(title);
  const selectedOutcome: CreationOutcome = invalid ? 'invalid' : (outcome as CreationOutcome);
  const frame = creationFrame(selectedOutcome, step, title);
  const confirmed = !invalid && outcome !== 'unavailable' && outcome !== 'lost';
  const end = confirmed ? 3 : 2;
  const busy = step > 0 && step < end;
  return (
    <Lab
      title="What does this response let us promise?"
      eyebrow="Experiment · create and confirm"
      className="wd-lab"
    >
      <Predict>
        Can a failed list refresh turn a confirmed successful creation into a failed creation?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Server and delivery outcome"
          value={outcome}
          disabled={busy}
          options={[
            choice('saved', 'Save and refresh succeed'),
            choice('unavailable', 'Save fails · 503'),
            choice('lost', 'Save succeeds; reply is lost'),
            choice('refresh-failed', '201 arrives; next list read fails'),
          ]}
          onChange={(value) => {
            setOutcome(value);
            setStep(0);
          }}
        />
        <label>
          Submitted title
          <Input
            aria-label="Submitted title"
            value={confirmed && step >= 2 ? '' : title}
            disabled={busy}
            onChange={(event) => {
              setTitle(event.target.value);
              setStep(0);
            }}
          />
        </label>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setTitle('   ');
            setStep(0);
          }}
        >
          Try a blank title
        </Button>
      </div>
      <p className="wd-caption">
        {Array.from(normalizeTitle(title)).length} / 80 Unicode code points after trimming spaces,
        tabs and line breaks.{' '}
        {busy
          ? 'Form frozen while this workflow is pending.'
          : step
            ? 'Controls restored after this workflow. Reset to replay from the same starting state.'
            : 'This model sends the chosen body directly to expose server validation, including a blank title.'}
      </p>
      <Steps
        labels={
          confirmed
            ? ['Send create', 'Receive create outcome', 'Receive list refresh']
            : ['Send create', 'Receive create outcome']
        }
        step={step}
      />
      <Frame frame={frame} />
      <div className="cb-controls">
        <Next disabled={step >= end} onClick={() => setStep(step + 1)}>
          {step === 0
            ? 'Send create request'
            : step === 1
              ? 'Receive create outcome'
              : 'Receive list refresh'}
        </Next>
        <Reset
          onClick={() => {
            setOutcome('saved');
            setTitle('Read notes');
            setStep(0);
          }}
        />
      </div>
    </Lab>
  );
}

export function WebActionsLab() {
  const [view, setView] = useState('completed');
  const [identity, setIdentity] = useState('index');
  const [operation, setOperation] = useState('patch');
  const [performed, setPerformed] = useState(false);
  const frame = actionFrame(
    view as TaskView,
    identity === 'id' ? 'id' : 'index',
    operation === 'delete' ? 'delete' : 'patch',
    performed,
  );
  return (
    <Lab
      title="The first visible row is not necessarily task 1"
      eyebrow="Experiment · identity, filtering and actions"
      className="wd-lab"
    >
      <Predict>
        In the completed view, the first row is task 7. What happens if its button sends ID 1?
      </Predict>
      <div className="cb-controls">
        <Select
          label="Displayed view"
          value={view}
          options={[
            choice('completed', 'Completed tasks'),
            choice('all', 'All tasks'),
            choice('open', 'Open tasks'),
            choice('reverse', 'All tasks, reversed'),
          ]}
          onChange={(value) => {
            setView(value);
            setPerformed(false);
          }}
        />
        <Select
          label="Button identifies its row by"
          value={identity}
          options={[
            choice('index', 'Visible position + 1 (fragile)'),
            choice('id', 'Record’s stable id'),
          ]}
          onChange={(value) => {
            setIdentity(value);
            setPerformed(false);
          }}
        />
        <Select
          label="Row operation"
          value={operation}
          options={[
            choice('patch', 'Set the opposite completion value'),
            choice('delete', 'Delete the selected record'),
          ]}
          onChange={(value) => {
            setOperation(value);
            setPerformed(false);
          }}
        />
      </div>
      <div className="wd-outputs">
        <Output
          label="Visible rows before action"
          value={frame.before.map((task) => `#${task.id} ${task.title}`).join('\n')}
        />
        <Output label="Request from first row’s button" value={frame.request} />
        <Output label="Authoritative records" value={JSON.stringify(frame.tasks, null, 2)} />
        <Output
          label="Refreshed current view"
          value={
            performed
              ? frame.after.map((task) => `#${task.id} ${task.title}`).join('\n') || '(empty view)'
              : '(not refreshed yet)'
          }
        />
      </div>
      <div className="cb-controls">
        <Next disabled={performed} onClick={() => setPerformed(true)}>
          Apply first row’s action
        </Next>
        <Reset
          onClick={() => {
            setView('completed');
            setIdentity('index');
            setOperation('patch');
            setPerformed(false);
          }}
        />
      </div>
      <Result tone={performed && frame.wrong ? 'warning' : 'neutral'}>
        {!performed
          ? 'Filtering changes the view, not the records’ identities. A closure can retain the correct task.id for this button.'
          : frame.wrong
            ? `The API followed the requested ID ${String(frame.target)}, addressing the wrong record. Correct HTTP cannot repair a wrong identity chosen by the UI.`
            : `${frame.response}. The current filter remains selected. A row leaving a filtered view is not necessarily a deletion.`}
      </Result>
    </Lab>
  );
}

export function WebOrderLab() {
  return (
    <TraceLab
      title="The last response is not always the newest view"
      predict="Start reading open tasks, then choose completed. What if the older open read arrives last?"
      label="Request coordination"
      options={[
        choice('overlap', 'Allow overlapping workflows'),
        choice('guard', 'Guard the whole workflow'),
      ]}
      steps={(value) =>
        value === 'guard'
          ? [
              'Start open read A',
              'Try completed read while A is pending',
              'Finish A and release its guard',
              'Start completed read B',
              'Finish B and release its guard',
            ]
          : [
              'Start open read A',
              'Start completed read B',
              'Deliver B first',
              'Deliver older A last',
            ]
      }
      frame={(value, step) => orderingFrame(value === 'guard', step)}
      note="Compare responses that arrive out of order. Completing each action and its refresh before starting the next keeps this page’s state consistent."
    />
  );
}
